import React from "react";
import { defineMessages, FormattedMessage } from "react-intl";
import { Alert } from "@yoast/ui-library";
import * as styles from "./DownloadsPageStyles.module.scss";
import useSubscriptionSummary from "../../hooks/useSubscriptionSummary";


const messages = defineMessages( {
	infoDownloadsOnlyProvisionedSubscriptions: {
		id: "downloadsPage.infoProvisionedNotShopify",
		defaultMessage: "You have sites with one or more of our provisioning partners. " +
			"The download and installation of these products are handled on purchase by them.",
	},
	infoDownloadsMixedProvisionedSubscriptions: {
		id: "downloadsPage.infoMixedProvisionedSubscriptions",
		defaultMessage: "On this page, you can download zip files related to your Yoast SEO products. " +
			"The download and installation of products purchased through third parties " +
			"are handled by their teams respectively.",
	},
} );

/**
 * Returns an Alert if certain conditions are met.
 *
 * The alert exists to explain third-party provisioners. WooCommerce is excluded because Yoast runs
 * that store, so telling the customer a third party handles their downloads would be wrong there.
 *
 * @returns {Boolean|false|ReactElement} An info alert or false if conditions are not met.
 */
export const ProvisionerDownloadAlert = () => {
	const { summary } = useSubscriptionSummary();
	const { isOnlyProvisioner, hasMixed, provisionerNames } = summary;

	/*
	 * Suppressed only when WooCommerce is the customer's *sole* provisioner and there is nothing mixed
	 * to explain. The length check matters because `every` is vacuously true on an empty array, which
	 * is now the common case: the summary omits Yoast-sold subscriptions from this list, where the
	 * selector it replaces contributed an empty-string name for each of them. Without `! hasMixed` a
	 * customer holding both WooCommerce and Yoast subscriptions would silently stop seeing the mixed
	 * alert they get today.
	 */
	const onlyProvisionedByWoo = provisionerNames.length > 0 &&
		provisionerNames.every( ( provisioner ) => provisioner === "WooCommerce" );

	if ( onlyProvisionedByWoo && ! hasMixed ) {
		return null;
	}

	if ( ! isOnlyProvisioner && ! hasMixed ) {
		return null;
	}

	return <Alert
		className={ styles.downloadsPageAlert }
		as="div"
		variant="info"
		role="alert"
	>
		{ isOnlyProvisioner &&
			<FormattedMessage { ...messages.infoDownloadsOnlyProvisionedSubscriptions } /> }
		{ hasMixed &&
			<FormattedMessage { ...messages.infoDownloadsMixedProvisionedSubscriptions } /> }
	</Alert>;
};

export default ProvisionerDownloadAlert;
